import type { ListView } from '@app/constants/list-views';
import type { FilterID } from '@app/features/next-soup/filters';
import { getMyTasksQuery } from '@app/features/next-soup/filters/configs/my-tasks';
import {
  defineQueryFilters,
  NIL_UUID,
  type Query,
} from '@app/features/next-soup/filters/filter-store';
import {
  enableCalendarUi,
  enableSnippets,
  enableSupportedSoupForeignEntities,
  isCalendarSearchUiEnabled,
  isFeatureEnabled,
} from '@core/constant/featureFlags';
import { PROPERTY_OPTION_IDS, SYSTEM_PROPERTY_IDS } from '@property/constants';
import type { Params } from '@service-storage/generated/schemas/params';
import { startOfDay, subWeeks } from 'date-fns';
import { CRM_TAB_PRESETS } from '../../crm/collection-presets';

type SoupFiltersPreset = {
  /** Filter data for server query */
  filters: Query;
  /** Client filters to apply */
  clientFilters: { and?: FilterID[]; or?: FilterID[] };
  /**
   * Initial group-by to apply when this tab is selected. Uses the same id
   * format consumed by `soup.grouping.setActiveGroupId` (e.g. `date`,
   * `entity_type`, `project`, or `property:<definition-id>`).
   */
  groupBy?: string;
  /**
   * Direction to order the server page in. Defaults to `desc` when absent,
   * matching every feed that reads newest-first.
   */
  sortDirection?: 'asc' | 'desc';
  /**
   * Server sort this tab's meaning requires (e.g. `touched_by_me`,
   * `notified_at`), taking precedence over the client sort state. Tabs that
   * force one usually also pin the client sort (`SoupView`'s
   * `initialClientSort`) so the server's ordering survives to the rendered
   * rows. Frecency is excluded: it is a different query flavor with its own
   * client handling, not a per-tab ordering.
   */
  sortMethod?: Exclude<NonNullable<Params['sort_method']>, 'frecency'>;
};

// Tab preset configuration types
export type PresetContext = {
  userId: string | undefined;
  /** True iff the current user has admin/owner team role. Drives
   * visibility of admin-only tabs (e.g. companies → hidden). */
  isTeamAdmin: boolean;
};

type TabPresetResolver = (ctx: PresetContext) => SoupFiltersPreset | undefined;

type TabConfig = Record<string, TabPresetResolver>;

export type ViewTabConfig = {
  default: string;
  tabs: TabConfig;
};

// Default statuses for the open-task tabs; keep the ids and include props in sync.
const OPEN_TASK_STATUS_FILTER_IDS: FilterID[] = [
  'task-not-started',
  'task-in-progress',
  'task-in-review',
];

const OPEN_TASK_STATUS_INCLUDE_PROPS = [
  {
    propertyId: SYSTEM_PROPERTY_IDS.STATUS,
    type: 'select' as const,
    value: PROPERTY_OPTION_IDS.STATUS.NOT_STARTED,
  },
  {
    propertyId: SYSTEM_PROPERTY_IDS.STATUS,
    type: 'select' as const,
    value: PROPERTY_OPTION_IDS.STATUS.IN_PROGRESS,
  },
  {
    propertyId: SYSTEM_PROPERTY_IDS.STATUS,
    type: 'select' as const,
    value: PROPERTY_OPTION_IDS.STATUS.IN_REVIEW,
  },
];

const getExcludedDocumentSubTypes = (...subTypes: string[]) =>
  isFeatureEnabled(enableSnippets) ? subTypes : [...subTypes, 'snippet'];

const getDisabledSnippetSubtypeExclude = (): Query['exclude'] =>
  isFeatureEnabled(enableSnippets) ? {} : { subType: ['snippet'] };

/** Filters for inbox/signal: not done, importance=true for emails, 2-week window */
const getInboxSignalFilters = () => {
  const twoWeeksAgo = subWeeks(startOfDay(new Date()), 2).toISOString();
  return defineQueryFilters({
    include: {
      documentDone: false,
      documentUpdatedAt: { gte: twoWeeksAgo },
      emailDone: false,
      emailImportance: true,
      emailUpdatedAt: { gte: twoWeeksAgo },
      channelDone: false,
      channelThreadDone: false,
      chatDone: false,
      chatUpdatedAt: { gte: twoWeeksAgo },
      folderDone: false,
      folderUpdatedAt: { gte: twoWeeksAgo },
      // Foreign entities (e.g. GitHub PRs) with a not-done notification.
      // Referencing `fef` also opts them into the signal query (otherwise
      // defineQueryFilters excludes unreferenced entity types). Rendering is
      // still gated on the supported-foreign-entities flag client-side.
      foreignEntitySource: ['github_pull_request'],
      foreignEntityDone: false,
      foreignEntityIncludesMe: true,
      emailShared: 'exclude',
      // Agent sessions the user was notified about (finished, asking, or a
      // mention). Off by default server-side, so this literal
      // is what surfaces them in the feed.
      includeAgentSessions: true,
      // Calendar events with a not-done notification (a fired event alarm).
      // Referencing `calf` opts the calendar arm into the signal query, which
      // `defineQueryFilters` otherwise excludes with a nil id filter.
      ...(isFeatureEnabled(enableCalendarUi)
        ? { calendarEventDone: false }
        : {}),
    },
    exclude: getDisabledSnippetSubtypeExclude(),
    emailView: 'inbox',
  });
};

/** Filters for inbox/noise: not done, importance=false for emails */
const getInboxNoiseFilters = () =>
  defineQueryFilters({
    include: {
      documentDone: false,
      emailDone: false,
      emailImportance: false,
      channelDone: false,
      channelThreadDone: false,
      chatDone: false,
      folderDone: false,
      emailShared: 'exclude',
    },
    exclude: getDisabledSnippetSubtypeExclude(),
    emailView: 'inbox',
  });

/**
 * Filters for the Recent view: the touched-by-me feed over everything the
 * all view shows. Documents, chats, folders, channels, and emails stay
 * unrestricted via `skipTargets` — the touched candidate query includes
 * every touchable type by default, and it rejects channel/email filter
 * trees outright (400), so even the usual NIL-id opt-in trees must not be
 * sent for those two. Calendar/CRM/foreign/channel-thread targets keep
 * their NIL exclusions; the touched query has no candidates of those types
 * and ignores their trees.
 */
const getRecentFilters = () =>
  defineQueryFilters(
    { exclude: getDisabledSnippetSubtypeExclude() },
    { skipTargets: ['df', 'cf', 'pf', 'chanf', 'ef'] }
  );

export const VIEW_TAB_PRESETS: Record<ListView, ViewTabConfig> = {
  recent: {
    default: 'all',
    tabs: {
      // One tab: everything the user has touched, newest own-touch first.
      // The server ordering is the product; the client sort is cleared by
      // the view registration so rows render in server order.
      all: () => ({
        filters: getRecentFilters(),
        clientFilters: { and: ['explicit-noise'] },
        sortMethod: 'touched_by_me',
      }),
    },
  },
  home: {
    default: 'signal',
    tabs: {
      // Signal and Noise order by when the viewer was last notified about
      // each row, not by the row's own recency: a comment on a week-old
      // task is today's news. The sort is also a filter (rows without a
      // notification are absent), which is what these tabs mean anyway.
      // The inbox's client sort (`notified_at`, see `SoupView`) keeps the
      // server order and buckets the date headers on the same timestamp.
      signal: () => ({
        filters: getInboxSignalFilters(),
        clientFilters: { and: ['inbox'] },
        groupBy: 'date',
        sortMethod: 'notified_at',
      }),
      noise: () => ({
        filters: getInboxNoiseFilters(),
        clientFilters: { and: ['noise'] },
        groupBy: 'date',
        sortMethod: 'notified_at',
      }),
      // All and Reminders keep their recency ordering. Named explicitly
      // because the inbox's client sort id is not an API sort method, so
      // without a preset value the API would fall back to created_at.
      all: () => ({
        filters: {
          // Calendar events are not rendered by Soup, and CRM companies are
          // not surfaced outside the Companies view.
          include: {
            calendarEventId: [NIL_UUID],
            crmCompanyId: [NIL_UUID],
            ...(isFeatureEnabled(enableSupportedSoupForeignEntities)
              ? { foreignEntitySource: ['github_pull_request'] }
              : {}),
            foreignEntityIncludesMe: true,
          },
          exclude: {
            documentId: [NIL_UUID],
            threadId: [NIL_UUID],
            channelId: [NIL_UUID],
            chatId: [NIL_UUID],
            folderId: [NIL_UUID],
            foreignEntityRecordId: isFeatureEnabled(
              enableSupportedSoupForeignEntities
            )
              ? [NIL_UUID]
              : [],
            ...getDisabledSnippetSubtypeExclude(),
          },
          emailView: 'all',
        },
        clientFilters: { and: ['explicit-noise'] },
        groupBy: 'date',
        sortMethod: 'updated_at',
      }),
    },
  },
  agents: {
    default: 'owned',
    tabs: {
      owned: (ctx) => {
        if (!ctx.userId) return undefined;
        return {
          filters: defineQueryFilters({
            include: {
              chatOwnerId: [ctx.userId],
              agentSessionOwnerId: [ctx.userId],
            },
          }),
          clientFilters: { and: ['agent'] },
        };
      },
      running: (ctx) => {
        if (!ctx.userId) return undefined;
        return {
          filters: defineQueryFilters({
            include: {
              chatOwnerId: [ctx.userId],
              agentSessionOwnerId: [ctx.userId],
            },
          }),
          clientFilters: { and: ['agent', 'owned-entity'] },
        };
      },
      shared: (ctx) => {
        if (!ctx.userId) return undefined;
        return {
          filters: defineQueryFilters({
            include: { includeAgentSessions: true },
            exclude: {
              chatOwnerId: [ctx.userId],
              agentSessionOwnerId: [ctx.userId],
            },
          }),
          clientFilters: { and: ['agent', 'shared-entity'] },
        };
      },
      routines: () => ({
        // Server returns nothing useful here — routines are merged
        // into the soup client-side via `additionalEntities`.
        filters: defineQueryFilters({}),
        clientFilters: { and: ['routine'] },
      }),
      skills: () => ({
        filters: defineQueryFilters({
          include: { subType: ['skill'] },
        }),
        clientFilters: { and: ['doc-skill'] },
      }),
    },
  },
  mail: {
    default: 'important',
    tabs: {
      // No 'no-drafts' on any thread-listing tab: a thread whose latest
      // message is a saved draft must stay in Signal/Noise/Calendar/Sent (it
      // also shows under Drafts). The server counts drafts toward is_signal
      // and inbox visibility for the same reason.
      important: () => ({
        filters: defineQueryFilters({
          include: {
            emailImportance: true,

            emailShared: 'exclude',
          },
          emailView: 'inbox',
        }),
        clientFilters: { and: ['email'] },
        groupBy: 'date',
      }),
      noise: () => ({
        filters: defineQueryFilters({
          include: {
            emailImportance: false,

            emailShared: 'exclude',
          },
          emailView: 'inbox',
        }),
        clientFilters: { and: ['email'] },
        groupBy: 'date',
      }),
      calendar: () => ({
        filters: defineQueryFilters({
          include: {
            emailShared: 'exclude',
            emailCalendarOnly: true,
          },
          emailView: 'all',
        }),

        clientFilters: { and: ['email'] },
        groupBy: 'date',
      }),
      drafts: () => ({
        filters: defineQueryFilters({
          exclude: { threadId: [NIL_UUID] },
          emailView: 'drafts',
        }),
        clientFilters: { and: ['email-drafts'] },
        groupBy: 'date',
      }),
      // No sender filter: the 'sent' view already scopes to messages with
      // is_sent = TRUE per linked inbox, which covers multi-inbox correctly
      // (a single sender address would drop secondary inboxes' sent mail).
      sent: () => ({
        filters: defineQueryFilters({
          emailView: 'sent',
        }),
        clientFilters: { and: ['email'] },
        groupBy: 'date',
      }),
      shared: () => ({
        filters: defineQueryFilters({
          include: { emailShared: 'only' },
          emailView: 'all',
        }),
        clientFilters: { and: ['email', 'shared-entity'] },
        groupBy: 'date',
      }),
      all: () => ({
        filters: defineQueryFilters({
          exclude: { threadId: [NIL_UUID] },
          emailView: 'all',
        }),
        clientFilters: { and: ['email'] },
        groupBy: 'date',
      }),
    },
  },
  documents: {
    default: 'owned',
    tabs: {
      owned: (ctx) => {
        if (!ctx.userId) return undefined;
        return {
          filters: defineQueryFilters({
            include: {
              documentOwnerId: [ctx.userId],
              isEmailAttachment: false,
            },
            exclude: { subType: getExcludedDocumentSubTypes('task') },
          }),
          clientFilters: { and: ['document-or-file', 'owned-entity'] },
        };
      },
      shared: (ctx) => {
        if (!ctx.userId) return undefined;
        return {
          filters: defineQueryFilters({
            include: {
              isEmailAttachment: false,
            },
            exclude: {
              subType: getExcludedDocumentSubTypes('task'),
              documentOwnerId: [ctx.userId],
            },
          }),
          clientFilters: { and: ['document-or-file', 'shared-entity'] },
        };
      },
      attachments: () => ({
        filters: defineQueryFilters({
          include: { isEmailAttachment: true },
        }),
        clientFilters: { and: ['document-or-file'] },
      }),
      folders: () => ({
        filters: defineQueryFilters({
          exclude: { folderId: [NIL_UUID] },
        }),
        clientFilters: { and: ['folders'] },
      }),
      all: () => ({
        filters: defineQueryFilters({
          exclude: { subType: getExcludedDocumentSubTypes('task') },
        }),
        clientFilters: { and: ['document-or-file'] },
      }),
    },
  },
  tasks: {
    default: 'my-tasks',
    tabs: {
      'my-tasks': (ctx) => {
        if (!ctx.userId) return undefined;
        const myTasksQuery = getMyTasksQuery(ctx.userId);
        return {
          filters: defineQueryFilters({
            ...myTasksQuery,
            include: {
              ...myTasksQuery.include,
              properties: [...OPEN_TASK_STATUS_INCLUDE_PROPS],
            },
          }),
          clientFilters: {
            and: ['task', 'my-tasks'],
            or: [...OPEN_TASK_STATUS_FILTER_IDS],
          },
          groupBy: `property:${SYSTEM_PROPERTY_IDS.PRIORITY}`,
        };
      },
      all: () => ({
        filters: defineQueryFilters({
          include: { subType: ['task'] },
        }),
        clientFilters: { and: ['task'] },
      }),
    },
  },
  channels: {
    default: 'recent',
    tabs: {
      recent: () => ({
        filters: defineQueryFilters({
          // Recent only shows channels the user is a participant of.
          include: {
            channelImportance: true,
            channelIsParticipant: [true],
          },
        }),
        clientFilters: { and: ['channels'] },
      }),
      people: () => ({
        filters: defineQueryFilters({
          include: { channelType: ['direct_message'] },
        }),
        clientFilters: { and: ['people'] },
      }),
      teams: () => ({
        filters: defineQueryFilters({
          // Both membership states: team channels of the user's teams they
          // haven't joined are listed too, with a Join affordance on the row.
          include: { channelIsParticipant: [true, false] },
          exclude: { channelType: ['direct_message'] },
        }),
        clientFilters: { and: ['teams'] },
      }),
    },
  },
  calls: {
    default: 'all',
    tabs: {
      all: () => ({
        filters: defineQueryFilters({}, { skipTargets: ['callf'] }),
        clientFilters: { and: ['calls'] },
      }),
      missed: () => ({
        filters: defineQueryFilters(
          {
            include: { callStatus: 'MISSED' },
          },
          { skipTargets: ['callf'] }
        ),
        clientFilters: { and: ['calls'] },
      }),
      unattended: () => ({
        filters: defineQueryFilters(
          {
            include: { callStatus: 'UNATTENDED' },
          },
          { skipTargets: ['callf'] }
        ),
        clientFilters: { and: ['calls'] },
      }),
    },
  },
  companies: CRM_TAB_PRESETS,
  folders: {
    default: 'owned',
    tabs: {
      owned: (ctx) => {
        if (!ctx.userId) return undefined;
        return {
          filters: defineQueryFilters({
            include: { folderOwnerId: [ctx.userId] },
          }),
          clientFilters: { and: ['folders', 'owned-entity'] },
        };
      },
      all: () => ({
        filters: defineQueryFilters({
          exclude: { folderId: [NIL_UUID] },
        }),
        clientFilters: { and: ['folders'] },
      }),
    },
  },
  search: {
    default: 'all',
    tabs: {
      all: () => ({
        // Temporary: search has no full-text index over foreign entities yet,
        // so always exclude them (matching no record id) until search supports
        // them. CRM and non-displayable channel-thread rows are NIL-excluded
        // the same way. Calendar events are not excluded — they carry a title
        // index of their own. `search-supported` mirrors these exclusions
        // client-side so entities that enter the soup cache outside this query
        // (e.g. websocket-driven inserts) don't surface in the search feed.
        filters: {
          include: {
            foreignEntityRecordId: [NIL_UUID],
            crmCompanyId: [NIL_UUID],
            channelThreadId: [NIL_UUID],
            // Events are title-indexed, so search returns them — but opening
            // one needs the calendar block, which the flag gates. Without it
            // a hit would render an inert row, so exclude the type instead.
            ...(isCalendarSearchUiEnabled()
              ? {}
              : { calendarEventId: [NIL_UUID] }),
          },
          exclude: getDisabledSnippetSubtypeExclude(),
        },
        clientFilters: { and: ['search-supported'] },
      }),
    },
  },
};

/** Views whose default tab requires user context */
type ContextRequiredView = 'agents' | 'documents' | 'tasks' | 'folders';

/** Views whose default tab works without user context */
type ContextOptionalView = Exclude<ListView, ContextRequiredView>;

/** Overload: views that don't require context */
export function getViewPreset(
  view: ContextOptionalView,
  tab?: string
): SoupFiltersPreset | undefined;

/** Overload: views that require user context */
export function getViewPreset(
  view: ContextRequiredView,
  tab: string | undefined,
  ctx: PresetContext
): SoupFiltersPreset | undefined;

/** Overload: any view with context */
export function getViewPreset(
  view: ListView,
  tab: string | undefined,
  ctx: PresetContext
): SoupFiltersPreset | undefined;

export function getViewPreset(
  view: ListView,
  tab?: string,
  ctx?: PresetContext
): SoupFiltersPreset | undefined {
  const config = VIEW_TAB_PRESETS[view];
  if (!config) return undefined;

  const tabId = tab ?? config.default;
  const resolver = config.tabs[tabId];
  if (!resolver) return undefined;

  const presetCtx: PresetContext = ctx ?? {
    userId: undefined,
    isTeamAdmin: false,
  };
  const resolved = resolver(presetCtx);
  if (resolved) return resolved;

  // Fallback: find first tab that works with provided context
  for (const fallbackResolver of Object.values(config.tabs)) {
    const fallback = fallbackResolver(presetCtx);
    if (fallback) return fallback;
  }

  return undefined;
}
